import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Filter, Grid3X3 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { PageLoader } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import {
  workloadSummaryApi,
  type WorkloadProjectRow,
} from "@/features/finances/api/workloadSummary";
import { useClickOutside } from "@/hooks/useClickOutside";

const TODAY = new Date();
// Default to the previous full month — same convention as other finance pages.
const PREV = (() => {
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
})();

const MONTHS_PL = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

// Stable palette for explicit employees within a project's stacked bar.
// 10 distinct hues — wraps for projects with more people, which is rare.
const PALETTE = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#65a30d",
  "#db2777",
  "#0d9488",
  "#7c3aed",
];

const AUTO_COLOR = "#9ca3af"; // gray for auto-allocation segment

type Mode = "bars" | "heatmap";

function MonthSelector({
  year,
  month,
  onChange,
}: {
  year: number;
  month: number;
  onChange: (y: number, m: number) => void;
}) {
  function shift(delta: number) {
    let y = year,
      m = month + delta;
    if (m < 1) {
      y--;
      m = 12;
    }
    if (m > 12) {
      y++;
      m = 1;
    }
    onChange(y, m);
  }
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => shift(-1)}
        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Poprzedni miesiąc"
      >
        ‹
      </button>
      <select
        value={month}
        onChange={(e) => onChange(year, Number(e.target.value))}
        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
      >
        {MONTHS_PL.map((m, i) => (
          <option key={i} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <input
        type="number"
        value={year}
        onChange={(e) => onChange(Number(e.target.value) || year, month)}
        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-20"
      />
      <button
        onClick={() => shift(+1)}
        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Następny miesiąc"
      >
        ›
      </button>
    </div>
  );
}

function colorForEmployee(allEmpIds: string[], empId: string) {
  const idx = allEmpIds.indexOf(empId);
  return PALETTE[(idx >= 0 ? idx : 0) % PALETTE.length];
}

// ── Bars view ────────────────────────────────────────────────────────────

function StackedBarsView({
  rows,
  includeFinances,
  employeeOrder,
}: {
  rows: WorkloadProjectRow[];
  includeFinances: boolean;
  employeeOrder: string[];
}) {
  if (rows.length === 0) {
    return (
      <p className="text-gray-500 text-sm">
        Brak aktywnych przedsięwzięć w wybranym okresie.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {rows.map((row) => {
        const explicitPct = Math.min(row.explicit_total_fraction * 100, 100);
        const autoPct = Math.max(100 - explicitPct, 0);
        // When finances are shown, size the bar segments by COST (złoty) rather
        // than the time fraction — a 20% time slice of a high earner is a large
        // cost, so segment LENGTHS must reflect the amounts, not the fractions.
        const total = Number(row.total_cost) || 0;
        const useCost = includeFinances && total > 0;
        const autoWidth = useCost
          ? (Number(row.auto_allocation_cost || 0) / total) * 100
          : autoPct;
        return (
          <div
            key={row.project_id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
          >
            <div className="flex items-baseline justify-between mb-1 gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">
                  <span className="font-mono text-xs text-gray-500 mr-2">
                    {row.project_short_name || "—"}
                  </span>
                  {row.project_name}
                </div>
                {row.client_name && (
                  <div className="text-xs text-gray-500 truncate">
                    {row.client_name}
                  </div>
                )}
              </div>
              {includeFinances && row.total_cost && (
                <div className="text-sm font-mono tabular-nums whitespace-nowrap">
                  {formatAmount(row.total_cost)}{" "}
                  <span className="text-xs text-gray-400">PLN</span>
                </div>
              )}
            </div>
            <div className="flex h-7 rounded overflow-hidden border border-gray-200 dark:border-gray-700">
              {row.explicit_allocations.map((a) => {
                const w = Math.min(a.fraction * 100, 100); // time-fraction % (label)
                const wWidth = useCost ? (Number(a.cost || 0) / total) * 100 : w;
                if (wWidth <= 0) return null;
                const color = colorForEmployee(employeeOrder, a.employee_id);
                return (
                  <div
                    key={a.employee_id}
                    title={
                      `${a.employee_name} — ${(a.fraction * 100).toFixed(1)}%` +
                      (includeFinances && a.cost
                        ? ` · ${formatAmount(a.cost)} PLN`
                        : "")
                    }
                    style={{ width: `${wWidth}%`, backgroundColor: color }}
                    className="flex items-center justify-center text-[10px] text-white font-medium px-1 overflow-hidden whitespace-nowrap"
                  >
                    {wWidth >= 6
                      ? includeFinances && a.cost
                        ? `${a.employee_name} · ${formatAmount(a.cost)} PLN`
                        : `${a.employee_name} · ${w.toFixed(0)}%`
                      : ""}
                  </div>
                );
              })}
              {autoWidth > 0 && (
                <div
                  title={
                    `Autoprzydział — ${autoPct.toFixed(1)}%` +
                    (includeFinances && row.auto_allocation_cost
                      ? ` · ${formatAmount(row.auto_allocation_cost)} PLN`
                      : "")
                  }
                  style={{ width: `${autoWidth}%`, backgroundColor: AUTO_COLOR }}
                  className="flex items-center justify-center text-[10px] text-white font-medium px-1 overflow-hidden whitespace-nowrap"
                >
                  {autoWidth >= 8
                    ? includeFinances && row.auto_allocation_cost
                      ? `auto · ${formatAmount(row.auto_allocation_cost)} PLN`
                      : `auto ${autoPct.toFixed(0)}%`
                    : ""}
                </div>
              )}
            </div>
            {/* Detail list under the bar */}
            {(row.explicit_allocations.length > 0 ||
              (includeFinances && row.auto_allocation_cost)) && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {row.explicit_allocations.map((a) => (
                  <span
                    key={a.employee_id}
                    className="inline-flex items-center gap-1"
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm"
                      style={{
                        backgroundColor: colorForEmployee(
                          employeeOrder,
                          a.employee_id,
                        ),
                      }}
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                      {a.employee_name}
                    </span>
                    <span className="text-gray-500 tabular-nums">
                      · {(a.fraction * 100).toFixed(1)}%
                    </span>
                    {includeFinances && a.cost && (
                      <span className="text-gray-400 tabular-nums">
                        · {formatAmount(a.cost)} PLN
                      </span>
                    )}
                  </span>
                ))}
                {includeFinances &&
                  row.auto_allocation_cost &&
                  Number(row.auto_allocation_cost) > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-sm"
                        style={{ backgroundColor: AUTO_COLOR }}
                      />
                      <span className="text-gray-700 dark:text-gray-200">
                        Autoprzydział
                      </span>
                      <span className="text-gray-400 tabular-nums">
                        · {formatAmount(row.auto_allocation_cost)} PLN
                      </span>
                    </span>
                  )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Heatmap view ─────────────────────────────────────────────────────────

type Cell =
  | {
      explicit: number;
      auto: number;
      total: number;
      explicitCost?: string;
      autoCost?: string;
      totalCost?: string;
    }
  | undefined;

function HeatmapView({
  rows,
  includeFinances,
  employees,
}: {
  rows: WorkloadProjectRow[];
  includeFinances: boolean;
  employees: { id: string; name: string; hasSalary: boolean }[];
}) {
  // Build matrix: emp.id → project.id → { explicit, auto, total }
  const matrix = useMemo(() => {
    const m: Record<string, Record<string, Cell>> = {};
    for (const row of rows) {
      for (const e of row.employees) {
        m[e.employee_id] = m[e.employee_id] ?? {};
        m[e.employee_id][row.project_id] = {
          explicit: e.explicit_fraction,
          auto: e.auto_fraction,
          total: e.total_fraction,
          explicitCost: e.explicit_cost,
          autoCost: e.auto_cost,
          totalCost: e.total_cost,
        };
      }
    }
    return m;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <p className="text-gray-500 text-sm">
        Brak aktywnych przedsięwzięć w wybranym okresie.
      </p>
    );
  }

  const cellBg = (cell: Cell) => {
    if (!cell || cell.total <= 0) return "transparent";
    const intensity = Math.min(Math.max(cell.total, 0), 1);
    const lightness = 92 - intensity * 52; // 92% (very light) → 40% (dark)
    return `hsl(217 91% ${lightness}%)`;
  };

  const cellText = (cell: Cell) => {
    if (!cell || cell.total <= 0) return "text-gray-300";
    return cell.total >= 0.5 ? "text-white" : "text-gray-800";
  };

  return (
    <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 text-left p-2 border-b border-gray-200 dark:border-gray-700 min-w-[180px]">
              Pracownik \ Projekt
            </th>
            {rows.map((row) => (
              <th
                key={row.project_id}
                className="p-2 border-b border-gray-200 dark:border-gray-700 align-bottom min-w-[80px]"
                title={`${row.project_name}${row.client_name ? " — " + row.client_name : ""}`}
              >
                <div className="font-mono font-semibold">
                  {row.project_short_name || "—"}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => (
            <tr
              key={emp.id}
              className="border-b border-gray-100 dark:border-gray-700/60"
            >
              <td
                className="sticky left-0 z-10 bg-white dark:bg-gray-800 p-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap"
                title={
                  emp.hasSalary
                    ? ""
                    : "Brak danych - pracownik bez kosztu płacy w tym miesiącu"
                }
              >
                {emp.name}
                {!emp.hasSalary && (
                  <sup className="ml-1 text-[9px] text-gray-400 align-super">
                    ∅
                  </sup>
                )}
              </td>
              {rows.map((row) => {
                const cell = matrix[emp.id]?.[row.project_id];
                const title =
                  cell && cell.total > 0
                    ? `${emp.name} → ${row.project_short_name}: ${(cell.total * 100).toFixed(1)}%` +
                      (cell.explicit > 0
                        ? ` (explicit ${(cell.explicit * 100).toFixed(1)}%)`
                        : "") +
                      (cell.auto > 0
                        ? ` (auto ${(cell.auto * 100).toFixed(1)}%)`
                        : "") +
                      (includeFinances && cell.totalCost
                        ? ` · ${formatAmount(cell.totalCost)} PLN`
                        : "")
                    : "";
                const explicitOnly =
                  cell && cell.explicit > 0 && cell.auto === 0;
                const autoOnly = cell && cell.auto > 0 && cell.explicit === 0;
                return (
                  <td
                    key={row.project_id}
                    className={`p-2 text-center tabular-nums ${cellText(cell)}`}
                    style={{ backgroundColor: cellBg(cell) }}
                    title={title}
                  >
                    {cell && cell.total > 0 ? (
                      <span>
                        {(cell.total * 100).toFixed(0)}%
                        {!explicitOnly && !autoOnly && (
                          <sup className="ml-0.5 text-[8px] opacity-70">m</sup>
                        )}
                        {autoOnly && (
                          <sup className="ml-0.5 text-[8px] opacity-70">a</sup>
                        )}
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {includeFinances && (
            <tr className="bg-gray-100 dark:bg-gray-900/60 font-semibold">
              <td className="sticky left-0 z-10 bg-gray-100 dark:bg-gray-900/60 p-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">
                Razem (PLN)
              </td>
              {rows.map((row) => (
                <td
                  key={row.project_id}
                  className="p-2 text-center text-gray-800 dark:text-gray-100 tabular-nums"
                >
                  {row.total_cost ? formatAmount(row.total_cost) : "—"}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-[10px] text-gray-500 mt-2 px-1">
        Wartość % to suma explicit + autoprzydziału dla danego pracownika i
        projektu.
        <sup className="mx-0.5">a</sup>= tylko autoprzydział,
        <sup className="mx-0.5">m</sup>= miks explicit + auto.
        <sup className="mx-0.5">∅</sup> przy nazwisku = brak kosztu płacy (nie
        kontrybuuje auto). Tooltip pokazuje szczegóły.
      </p>
    </div>
  );
}

// ── Filter popover ───────────────────────────────────────────────────────

interface FilterItem {
  id: string;
  label: string;
  sublabel?: string;
}

function FilterPopover({
  title,
  items,
  hiddenIds,
  onChange,
  buttonLabel,
}: {
  title: string;
  items: FilterItem[];
  hiddenIds: Set<string>;
  onChange: (next: string[]) => void;
  buttonLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        (i.sublabel ?? "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const visibleCount = items.length - hiddenIds.size;
  const allVisible = hiddenIds.size === 0;
  const allHidden = visibleCount === 0;

  function toggle(id: string) {
    const next = new Set(hiddenIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  }

  function showAll() {
    onChange([]);
  }
  function hideAll() {
    onChange(items.map((i) => i.id));
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border transition-colors ${
          hiddenIds.size > 0
            ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/5"
            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        }`}
      >
        <Filter size={14} />
        {buttonLabel} {visibleCount}/{items.length}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-2xl p-3 w-72 max-h-[70vh] flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">{title}</span>
            <div className="flex gap-1 text-xs">
              <button
                onClick={showAll}
                disabled={allVisible}
                className="px-1.5 py-0.5 rounded text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700 disabled:text-gray-400 disabled:hover:bg-transparent"
              >
                Pokaż wszystkich
              </button>
              <button
                onClick={hideAll}
                disabled={allHidden}
                className="px-1.5 py-0.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:text-gray-300 disabled:hover:bg-transparent"
              >
                Ukryj wszystkich
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Szukaj..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 mb-2"
          />
          <div className="overflow-y-auto flex-1 min-h-0">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-500 p-2">
                Brak pasujących pozycji.
              </p>
            ) : (
              filtered.map((it) => {
                const visible = !hiddenIds.has(it.id);
                return (
                  <label
                    key={it.id}
                    className="flex items-center gap-2 px-1 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={() => toggle(it.id)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">
                        {it.label}
                      </span>
                      {it.sublabel && (
                        <span className="block text-[10px] text-gray-500 truncate">
                          {it.sublabel}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────

export default function WorkloadSummaryPage() {
  const user = useAuthStore((s) => s.user);
  const [year, setYear] = useState(PREV.year);
  const [month, setMonth] = useState(PREV.month);
  const [mode, setMode] = useState<Mode>("bars");
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["workload-summary", year, month],
    queryFn: () => workloadSummaryApi.get(year, month),
    enabled: !!user,
  });

  // ── Persistent per-user filter preferences ────────────────────────────
  const queryClient = useQueryClient();
  const { data: prefsServer } = useQuery({
    queryKey: ["workload-summary-prefs"],
    queryFn: () => workloadSummaryApi.getPreferences(),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  // Local mirror lets the UI react instantly; mutation persists to backend.
  const [hiddenEmployeeIds, setHiddenEmployeeIds] = useState<string[]>([]);
  const [hiddenProjectIds, setHiddenProjectIds] = useState<string[]>([]);
  const initialized = useRef(false);

  useEffect(() => {
    if (prefsServer && !initialized.current) {
      setHiddenEmployeeIds(prefsServer.hidden_employee_ids ?? []);
      setHiddenProjectIds(prefsServer.hidden_project_ids ?? []);
      initialized.current = true;
    }
  }, [prefsServer]);

  const prefsMutation = useMutation({
    mutationFn: (payload: {
      hidden_employee_ids?: string[];
      hidden_project_ids?: string[];
    }) => workloadSummaryApi.putPreferences(payload),
    onSuccess: (resp) => {
      queryClient.setQueryData(["workload-summary-prefs"], resp);
    },
  });

  function updateHiddenEmployees(next: string[]) {
    setHiddenEmployeeIds(next);
    prefsMutation.mutate({ hidden_employee_ids: next });
  }
  function updateHiddenProjects(next: string[]) {
    setHiddenProjectIds(next);
    prefsMutation.mutate({ hidden_project_ids: next });
  }

  const hiddenEmployeeSet = useMemo(
    () => new Set(hiddenEmployeeIds),
    [hiddenEmployeeIds],
  );
  const hiddenProjectSet = useMemo(
    () => new Set(hiddenProjectIds),
    [hiddenProjectIds],
  );

  const filteredRows = useMemo(() => {
    let rows = data?.projects ?? [];
    // Persistent filter — hide projects the user opted out of.
    if (hiddenProjectSet.size > 0) {
      rows = rows.filter((r) => !hiddenProjectSet.has(r.project_id));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.project_name.toLowerCase().includes(q) ||
          r.project_short_name.toLowerCase().includes(q) ||
          r.client_name.toLowerCase().includes(q) ||
          (r.employees ?? []).some((e) =>
            e.employee_name.toLowerCase().includes(q),
          ),
      );
    }
    return rows;
  }, [data, search, hiddenProjectSet]);

  // Wiersze heatmapy: preferuj top-level `all_employees` (zawiera też osoby
  // bez żadnego wkładu — np. brak salary i brak przydziału, ale są aktywni).
  // Fallback dla starszego backendu: agregacja z projektów.
  const employees = useMemo(() => {
    if (data?.all_employees && data.all_employees.length > 0) {
      return [...data.all_employees]
        .map((e) => ({ id: e.id, name: e.name, hasSalary: e.has_salary }))
        .sort((a, b) => a.name.localeCompare(b.name, "pl"));
    }
    const seen = new Set<string>();
    const result: { id: string; name: string; hasSalary: boolean }[] = [];
    for (const row of data?.projects ?? []) {
      for (const e of row.employees ?? []) {
        if (!seen.has(e.employee_id)) {
          seen.add(e.employee_id);
          result.push({
            id: e.employee_id,
            name: e.employee_name,
            hasSalary: true,
          });
        }
      }
      for (const a of row.explicit_allocations) {
        if (!seen.has(a.employee_id)) {
          seen.add(a.employee_id);
          result.push({
            id: a.employee_id,
            name: a.employee_name,
            hasSalary: true,
          });
        }
      }
    }
    result.sort((a, b) => a.name.localeCompare(b.name, "pl"));
    return result;
  }, [data]);

  // Filtered employees list — what the heatmap actually renders.
  const visibleEmployees = useMemo(
    () => employees.filter((e) => !hiddenEmployeeSet.has(e.id)),
    [employees, hiddenEmployeeSet],
  );

  const employeeOrder = useMemo(() => employees.map((e) => e.id), [employees]);

  // Aggregate KPIs (CEO only).
  const totals = useMemo(() => {
    if (!data?.include_finances) return null;
    const rows = data.projects;
    let totalCost = 0;
    let autoCost = 0;
    for (const r of rows) {
      totalCost += Number(r.total_cost ?? 0);
      autoCost += Number(r.auto_allocation_cost ?? 0);
    }
    const explicitCost = totalCost - autoCost;
    return { totalCost, autoCost, explicitCost };
  }, [data]);

  if (isLoading) return <PageLoader />;
  if (error) {
    return (
      <div className="p-6 text-red-600">
        Nie udało się załadować podsumowania rozkładu pracy.
      </div>
    );
  }

  const includeFinances = data?.include_finances ?? false;

  return (
    <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Rozkład pracy – podsumowanie
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Procentowy przydział pracowników do przedsięwzięć w wybranym
            miesiącu.{" "}
            {includeFinances
              ? "Kwoty PLN obejmują koszt pracownika dla każdego przydziału oraz koszt autoprzydziału (proporcjonalny rozdział reszty etatu)."
              : "Widok bez kwot — dyrektor techniczny."}
          </p>
        </div>
        <MonthSelector
          year={year}
          month={month}
          onChange={(y, m) => {
            setYear(y);
            setMonth(m);
          }}
        />
      </div>

      {/* KPI tiles — CEO only */}
      {includeFinances && totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <div className="text-xs text-gray-500">Łączny koszt płac</div>
            <div className="text-lg font-semibold tabular-nums">
              {formatAmount(totals.totalCost.toFixed(2))}{" "}
              <span className="text-xs text-gray-400">PLN</span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <div className="text-xs text-gray-500">Z explicit przydziału</div>
            <div className="text-lg font-semibold tabular-nums">
              {formatAmount(totals.explicitCost.toFixed(2))}{" "}
              <span className="text-xs text-gray-400">PLN</span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <div className="text-xs text-gray-500">Z autoprzydziału</div>
            <div className="text-lg font-semibold tabular-nums">
              {formatAmount(totals.autoCost.toFixed(2))}{" "}
              <span className="text-xs text-gray-400">PLN</span>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar: mode toggle + search + persistent filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden self-start">
          <button
            onClick={() => setMode("bars")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
              mode === "bars"
                ? "bg-[var(--color-primary)] text-white"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
            data-testid="mode-bars"
          >
            <BarChart3 size={14} /> Stacked bary
          </button>
          <button
            onClick={() => setMode("heatmap")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
              mode === "heatmap"
                ? "bg-[var(--color-primary)] text-white"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
            data-testid="mode-heatmap"
          >
            <Grid3X3 size={14} /> Heatmapa
          </button>
        </div>
        <input
          type="text"
          placeholder="Szukaj: projekt, klient lub pracownik..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 sm:max-w-md border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-800"
        />
        <FilterPopover
          title="Pracownicy widoczni w heatmapie"
          buttonLabel="Pracownicy"
          items={employees.map((e) => ({
            id: e.id,
            label: e.name,
            sublabel: e.hasSalary ? undefined : "brak kosztu płacy",
          }))}
          hiddenIds={hiddenEmployeeSet}
          onChange={updateHiddenEmployees}
        />
        <FilterPopover
          title="Projekty widoczne w heatmapie"
          buttonLabel="Projekty"
          items={(data?.projects ?? []).map((p) => ({
            id: p.project_id,
            label: `${p.project_short_name || "—"} · ${p.project_name}`,
            sublabel: p.client_name || undefined,
          }))}
          hiddenIds={hiddenProjectSet}
          onChange={updateHiddenProjects}
        />
      </div>

      {/* Visualization */}
      {mode === "bars" ? (
        <StackedBarsView
          rows={filteredRows}
          includeFinances={includeFinances}
          employeeOrder={employeeOrder}
        />
      ) : (
        <HeatmapView
          rows={filteredRows}
          includeFinances={includeFinances}
          employees={visibleEmployees}
        />
      )}
    </div>
  );
}
